import React, { useState, useRef, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';

function MerchantReportPage() {
  const { authFetch } = useAuth();
  const gridRef = useRef(null);

  const today = new Date();
  const currentMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;

  const [month, setMonth] = useState(currentMonth);
  const [type, setType] = useState('B2C');
  const [viewMode, setViewMode] = useState('regular'); // 'regular' | 'h30' | 'compare'
  const [loading, setLoading] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [columnDefs, setColumnDefs] = useState([]);
  const [summary, setSummary] = useState(null);

  const CurrencyCellRenderer = (params) => {
    if (params.value == null || params.value === 0) return '';
    const formatted = new Intl.NumberFormat('id-ID').format(params.value);
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
        <span>Rp</span>
        <span>{formatted}</span>
      </div>
    );
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const period = `${month}-01`;
      const url = `/api/merchant-report/pivot?type=${type}&period=${period}&view=${viewMode}`;
      const response = await authFetch(url);
      const data = await response.json();

      if (data.error) {
        toast.error(data.error);
        setRowData([]);
        setColumnDefs([]);
        return;
      }

      // Build column definitions
      const cols = [
        { field: 'merchant', headerName: 'Merchant', pinned: 'left', width: 160, filter: true }
      ];

      // PGA columns - hide columns where all merchants have 0/null data
      const rows = data.rows || [];
      for (const pga of data.columns) {
        const hasData = rows.some(row => row[pga] && row[pga] > 0);
        if (!hasData) continue; // skip empty columns
        cols.push({
          field: pga,
          headerName: pga,
          width: 160,
          type: 'numericColumn',
          cellRenderer: CurrencyCellRenderer,
          cellStyle: params => params.value > 0 ? { padding: '0 12px' } : { color: '#ccc' }
        });
      }

      // TOTAL + FEE
      cols.push({
        field: 'TOTAL',
        headerName: 'TOTAL',
        width: 180,
        type: 'numericColumn',
        cellRenderer: CurrencyCellRenderer,
        cellStyle: { fontWeight: 'bold', background: '#f0f7ff', padding: '0 12px' }
      });

      const feeLabel = type === 'B2C' ? 'Fee (0.5%)' : 'Fee (1.5%)';
      cols.push({
        field: 'FEE',
        headerName: feeLabel,
        width: 170,
        type: 'numericColumn',
        cellRenderer: CurrencyCellRenderer,
        cellStyle: { background: '#fff8f0', padding: '0 12px' }
      });

      // H+30 columns
      if (data.show_h30) {
        cols.push({
          field: 'H30_TOTAL',
          headerName: 'H+30 Total',
          width: 170,
          type: 'numericColumn',
          cellRenderer: CurrencyCellRenderer,
          cellStyle: { background: '#f0fff0', padding: '0 12px' }
        });
        cols.push({
          field: 'DIFF',
          headerName: 'DIFF',
          width: 170,
          type: 'numericColumn',
          cellRenderer: (params) => {
            if (params.value == null) return '-';
            const formatted = new Intl.NumberFormat('id-ID').format(params.value);
            return (
              <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <span>Rp</span>
                <span>{formatted}</span>
              </div>
            );
          },
          cellStyle: params => {
            if (params.value == null) return { color: '#999' };
            return params.value > 0 ? { color: 'green', fontWeight: 'bold', padding: '0 12px' } : params.value < 0 ? { color: 'red', fontWeight: 'bold', padding: '0 12px' } : { padding: '0 12px' };
          }
        });
        cols.push({
          field: 'FEE_DIFF',
          headerName: 'Fee DIFF',
          width: 170,
          type: 'numericColumn',
          cellRenderer: (params) => {
            if (params.value == null) return '-';
            const formatted = new Intl.NumberFormat('id-ID').format(params.value);
            return (
              <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <span>Rp</span>
                <span>{formatted}</span>
              </div>
            );
          },
          cellStyle: params => {
            if (params.value == null) return { color: '#999' };
            return params.value > 0 ? { color: 'green', padding: '0 12px' } : params.value < 0 ? { color: 'red', padding: '0 12px' } : { padding: '0 12px' };
          }
        });
      }

      setColumnDefs(cols);
      setRowData(data.rows || []);
      setSummary(data.total_row || null);
      toast.success(`Data loaded: ${(data.rows || []).length} merchants`);
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [authFetch, month, type, viewMode]);

  const exportCsv = () => {
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.exportDataAsCsv({
        fileName: `merchant_report_${type}_${month}.csv`
      });
    }
  };

  const defaultColDef = {
    sortable: true,
    resizable: true,
  };

  // Append total row as pinned bottom
  const pinnedBottomRowData = summary ? [summary] : [];

  return (
    <div style={{ padding: '20px' }}>
      <h1>Merchant Report</h1>

      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div>
          <label style={{ marginRight: '6px', fontWeight: '500' }}>Type:</label>
          <select value={type} onChange={(e) => setType(e.target.value)} style={{ padding: '6px 10px' }}>
            <option value="B2C">B2C</option>
            <option value="B2B">B2B</option>
          </select>
        </div>

        <div>
          <label style={{ marginRight: '6px', fontWeight: '500' }}>Periode:</label>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} style={{ padding: '6px 10px' }} />
        </div>

        <div>
          <label style={{ marginRight: '6px', fontWeight: '500' }}>Data:</label>
          <select value={viewMode} onChange={(e) => setViewMode(e.target.value)} style={{ padding: '6px 10px' }}>
            <option value="regular">Regular</option>
            <option value="h30">H+30 Only</option>
            <option value="compare">Regular + H+30 (DIFF)</option>
          </select>
        </div>

        <button onClick={loadData} disabled={loading} style={{ padding: '8px 16px', background: '#007bff', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
          {loading ? 'Loading...' : 'Load Data'}
        </button>

        {rowData.length > 0 && (
          <button onClick={exportCsv} style={{ padding: '8px 16px', background: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            Export CSV
          </button>
        )}
      </div>

      {rowData.length > 0 && (
        <div className="ag-theme-alpine" style={{ width: '100%', height: 'calc(100vh - 200px)' }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            pinnedBottomRowData={pinnedBottomRowData}
            animateRows={true}
            enableCellTextSelection={true}
          />
        </div>
      )}

      {!loading && rowData.length === 0 && (
        <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>
          Pilih periode dan klik "Load Data" untuk menampilkan report.
        </div>
      )}
    </div>
  );
}

export default MerchantReportPage;
